// src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import AttendancePage from "./pages/AttendancePage";
import ClassesPage from "./pages/ClassesPage";
import MenuItemsPage from "./pages/MenuItemsPage";
import ConfigPage from "./pages/ConfigPage";
import StockPage from "./pages/StockPage";
import ReportsPage from "./pages/ReportsPage";
import VisitorsPage from "./pages/VisitorsPage";
import SettingsPage from "./pages/SettingsPage";
import AcademicYearPage from "./pages/AcademicYearPage";

// ── Back button security ─────────────────────────────────────
// After logout, push a "barrier" into history so back button
// takes user to login, not dashboard.
function BackButtonGuard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loggedOut = sessionStorage.getItem("logged_out");
    // If not logged in and trying to access a protected path → force to login
    if (!user && loggedOut) {
      const protectedPaths = [
        "/dashboard",
        "/attendance",
        "/classes",
        "/menu-items",
        "/config",
        "/stock",
        "/reports",
        "/visitors",
        "/settings",
        "/academic-year",
      ];
      if (protectedPaths.some((p) => location.pathname.startsWith(p))) {
        navigate("/login", { replace: true });
      }
    }
  }, [location, user, navigate]);

  // Push login into history stack on logout so back button = login page
  useEffect(() => {
    const loggedOut = sessionStorage.getItem("logged_out");
    if (!user && loggedOut) {
      // Replace current history entry with login page
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  return null;
}

// ── Protected Route ──────────────────────────────────────────
function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#080f1e",
          color: "rgba(255,255,255,0.5)",
          fontFamily: "Noto Sans Kannada",
          fontSize: "1rem",
        }}
      >
        ಲೋಡ್ ಆಗುತ್ತಿದೆ...
      </div>
    );

  // Not logged in → go to login (replace: true prevents back-button return)
  if (!user) return <Navigate to="/login" replace />;

  // Wrong role
  if (roles && !roles.includes(user.role))
    return <Navigate to="/dashboard" replace />;

  return <Layout>{children}</Layout>;
}

// ── Public Route (redirect logged-in users away) ─────────────
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Already logged in → go to dashboard
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

// ── All Routes ───────────────────────────────────────────────
function AppRoutes() {
  return (
    <>
      <BackButtonGuard />
      <Routes>
        {/* Public */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected - all roles */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance"
          element={
            <ProtectedRoute roles={["admin", "principal", "clerk"]}>
              <AttendancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock"
          element={
            <ProtectedRoute roles={["admin", "principal", "clerk"]}>
              <StockPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/visitors"
          element={
            <ProtectedRoute roles={["admin", "principal", "clerk"]}>
              <VisitorsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin only */}
        <Route
          path="/classes"
          element={
            <ProtectedRoute roles={["admin"]}>
              <ClassesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu-items"
          element={
            <ProtectedRoute roles={["admin"]}>
              <MenuItemsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/config"
          element={
            <ProtectedRoute roles={["admin"]}>
              <ConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute roles={["admin"]}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/academic-year"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AcademicYearPage />
            </ProtectedRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              background: "rgba(10,22,40,0.97)",
              color: "#fff",
              border: "1px solid rgba(74,222,128,0.25)",
              borderRadius: "12px",
              fontFamily: "'Noto Sans Kannada', sans-serif",
              fontSize: "0.88rem",
              backdropFilter: "blur(12px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            },
            success: { iconTheme: { primary: "#4ade80", secondary: "#fff" } },
            error: { iconTheme: { primary: "#f87171", secondary: "#fff" } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
